import { ChangeEvent, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PermissionButton } from "@/components/ui/permission-button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { apiRequest } from "@/lib/queryClient";
import { formatPhoneInputValue } from "@/lib/phone";
import { insertProspectSchema, type Prospect } from "@shared/schema";
import { useToast } from "@/hooks/use-toast";
import { AddressFields } from "@/components/forms/AddressFields";
import {
  composeAddressLine,
  defaultCountry,
  emptyAddressFields,
  normalizeAddressFields,
} from "@/lib/address";
import {
  Plus,
  Search,
  MessageSquare,
  Trash2,
  Building,
  DollarSign,
  Target,
  Calendar,
  Edit,
  Download,
  Upload,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
} from "lucide-react";
import { Link } from "wouter";
import { z } from "zod";
import { calendarService } from "@/lib/calendarService";
import { messagesService } from "@/lib/messagesService";
import { SOURCE_OPTIONS, formatSource } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

type SortField = "name" | "contactEmail" | "status" | "priority" | "expectedValue" | "source";
type SortDirection = "asc" | "desc" | null;

export default function Prospects() {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterPriority, setFilterPriority] = useState<string>("all");
  const [filterSource, setFilterSource] = useState<string>("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [editingProspect, setEditingProspect] = useState<Prospect | null>(null);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        setSortField(null);
        setSortDirection(null);
      } else {
        setSortDirection("asc");
      }
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ChevronsUpDown className="h-4 w-4 ml-1 text-gray-400" />;
    }
    if (sortDirection === "asc") {
      return <ChevronUp className="h-4 w-4 ml-1 text-blue-600" />;
    }
    return <ChevronDown className="h-4 w-4 ml-1 text-blue-600" />;
  };

  const exportTemplate = () => {
    const headers = [
      "Company Name",
      "Contact Email",
      "Contact Phone",
      "Address Line 1",
      "Address Line 2",
      "City",
      "State",
      "Zip Code",
      "County",
      "Country",
      "Status",
      "Source",
      "Priority",
      "Expected Value",
      "Expected Close Date",
      "Notes",
    ];
    const exampleRow = [
      "Example Company LLC",
      "contact@example.com",
      "(555) 123-4567",
      "123 Main Street",
      "Suite 100",
      "New York",
      "NY",
      "10001",
      "New York",
      "United States",
      "lead",
      "referral",
      "medium",
      "50000",
      "2026-06-01",
      "Sample notes here",
    ];
    const csvContent = [headers.join(","), exampleRow.join(",")].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "prospect_import_template.csv";
    link.click();
    URL.revokeObjectURL(link.href);
    toast({
      title: "Template Downloaded",
      description: "Fill in the template and upload it to import prospects.",
    });
  };

  const handleImportFile = async () => {
    if (!importFile) {
      toast({
        title: "No File Selected",
        description: "Please select a CSV file to import.",
        variant: "destructive",
      });
      return;
    }

    setIsImporting(true);
    try {
      const text = await importFile.text();
      const lines = text.split("\n").filter((line) => line.trim());
      if (lines.length < 2) {
        throw new Error("CSV file must have a header row and at least one data row.");
      }

      const headers = lines[0].split(",").map((h) => h.trim().toLowerCase().replace(/"/g, ""));
      const dataRows = lines.slice(1);

      const headerMap: Record<string, string> = {
        "company name": "name",
        "contact email": "contactEmail",
        "contact phone": "contactPhone",
        "address line 1": "address1",
        "address line 2": "address2",
        "city": "city",
        "state": "state",
        "zip code": "zipCode",
        "county": "county",
        "country": "country",
        "status": "status",
        "source": "source",
        "priority": "priority",
        "expected value": "expectedValue",
        "expected close date": "expectedCloseDate",
        "notes": "notes",
      };

      const prospects: any[] = [];
      for (const row of dataRows) {
        const values = row.split(",").map((v) => v.trim().replace(/^"|"$/g, ""));
        const prospect: any = {};
        
        headers.forEach((header, index) => {
          const fieldName = headerMap[header];
          if (fieldName && values[index]) {
            prospect[fieldName] = values[index];
          }
        });

        if (prospect.name && prospect.contactEmail) {
          if (!prospect.status) prospect.status = "lead";
          if (!prospect.priority) prospect.priority = "medium";
          if (!prospect.country) prospect.country = "United States";
          prospects.push(prospect);
        }
      }

      if (prospects.length === 0) {
        throw new Error("No valid prospects found in the file. Each row must have at least Company Name and Contact Email.");
      }

      const response = await apiRequest("POST", "/api/prospects/import", { prospects });
      
      queryClient.invalidateQueries({ queryKey: ["/api/prospects"] });
      setIsImportDialogOpen(false);
      setImportFile(null);
      
      toast({
        title: "Import Successful",
        description: `Successfully imported ${prospects.length} prospect(s).`,
      });
    } catch (error: any) {
      toast({
        title: "Import Failed",
        description: error.message || "Failed to import prospects. Please check your file format.",
        variant: "destructive",
      });
    } finally {
      setIsImporting(false);
    }
  };

  // Helper function to convert date to input format
  const formatDateForInput = (date: any) => {
    if (!date) return "";
    if (typeof date === "string") {
      return date.split("T")[0];
    }
    return new Date(date).toISOString().split("T")[0];
  };

  const { data: prospects = [], isLoading } = useQuery<Prospect[]>({
    queryKey: ["/api/prospects"],
  });

  // Fetch user's team assignments for producer auto-assignment
  interface UserTeam {
    teamId: number;
    teamName: string;
    teamLeadId: number | null;
    teamLeadName: string | null;
    isTeamLead: boolean;
  }
  const { data: userTeams = [] } = useQuery<UserTeam[]>({
    queryKey: ["/api/user/my-teams"],
  });
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);

  // Fetch all prospect account managers to display primary agent in the list
  interface ProspectAccountManager {
    id: number;
    prospectId: number;
    userId: string;
    role: string;
    employeeName: string | null;
  }
  const { data: prospectAccountManagers = [] } = useQuery<ProspectAccountManager[]>({
    queryKey: ["/api/prospect-account-managers"],
  });

  // Create a map of prospect ID to primary producer name
  const primaryProducerMap = new Map<number, string>();
  prospectAccountManagers.forEach((manager) => {
    if (manager.role === "primary_producer" && manager.employeeName) {
      primaryProducerMap.set(manager.prospectId, manager.employeeName);
    }
  });

  const form = useForm({
    resolver: zodResolver(insertProspectSchema),
    defaultValues: {
      name: "",
      primaryContactName: "",
      contactEmail: "",
      contactPhone: "",
      address: "",
      // Normalized address fields (composed into address on submit)
      ...emptyAddressFields(),
      status: "lead",
      source: "",
      priority: "medium",
      notes: "",
      expectedValue: "",
      expectedCloseDate: "",
      lastContactDate: "",
    },
  });

  const handlePhoneFieldChange = (
    event: ChangeEvent<HTMLInputElement>,
    onChange: (value: string) => void,
  ) => {
    const formattedValue = formatPhoneInputValue(event.target.value);
    onChange(formattedValue);
  };

  const handleSubmitForm = (values: any) => {
    const addressLine = composeAddressLine(values);
    const payload: any = {
      ...values,
      address: addressLine || values.address || "",
    };
    if (editingProspect) {
      updateProspectMutation.mutate({ id: editingProspect.id, data: payload });
    } else {
      // Include selected team for producer auto-assignment
      if (selectedTeamId) {
        payload.teamId = selectedTeamId;
      }
      createProspectMutation.mutate(payload);
    }
  };

  const createProspectMutation = useMutation({
    mutationFn: async (data: any) => {
      return apiRequest("POST", "/api/prospects", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/prospects"] });
      queryClient.invalidateQueries({ queryKey: ["/api/email/contacts"] });
      setIsDialogOpen(false);
      form.reset();
      toast({
        title: "Success",
        description: "Prospect created successfully",
      });
    },
    onError: (error: any) => {
      console.error("Prospect creation error:", error);

      // Handle validation errors by setting form field errors
      if (error?.errors && Array.isArray(error.errors)) {
        error.errors.forEach((err: any) => {
          if (err.path && err.path.length > 0) {
            const fieldName = err.path[0];
            form.setError(fieldName, {
              type: "manual",
              message: err.message,
            });
          }
        });
        // Don't show toast for validation errors - fields will be highlighted
        return;
      }

      // For non-validation errors, show toast
      const errorMessage = error?.details || error?.message || "Failed to create prospect";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  const updateProspectMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      return apiRequest("PUT", `/api/prospects/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/prospects"] });
      setIsDialogOpen(false);
      setEditingProspect(null);
      form.reset();
      toast({
        title: "Success",
        description: "Prospect updated successfully",
      });
    },
    onError: (error: any) => {
      console.error("Prospect update error:", error);

      // Handle validation errors by setting form field errors
      if (error?.errors && Array.isArray(error.errors)) {
        error.errors.forEach((err: any) => {
          if (err.path && err.path.length > 0) {
            const fieldName = err.path[0];
            form.setError(fieldName, {
              type: "manual",
              message: err.message,
            });
          }
        });
        // Don't show toast for validation errors - fields will be highlighted
        return;
      }

      // For non-validation errors, show toast
      const errorMessage = error?.details || error?.message || "Failed to update prospect";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  const deleteProspectMutation = useMutation({
    mutationFn: async (id: number) => {
      return apiRequest("DELETE", `/api/prospects/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/prospects"] });
      toast({
        title: "Success",
        description: "Prospect deleted successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to delete prospect",
        variant: "destructive",
      });
    },
  });

  const handleEdit = (prospect: Prospect) => {
    setEditingProspect(prospect);
    const addressFields = normalizeAddressFields(prospect);
    form.reset({
      name: prospect.name,
      primaryContactName: (prospect as any).primaryContactName || "",
      contactEmail: prospect.contactEmail,
      contactPhone: prospect.contactPhone || "",
      address: prospect.address || "",
      ...addressFields,
      status: prospect.status,
      source: prospect.source || "",
      priority: prospect.priority,
      notes: prospect.notes || "",
      expectedValue: prospect.expectedValue || "",
      expectedCloseDate: formatDateForInput(prospect.expectedCloseDate),
      lastContactDate: formatDateForInput(prospect.lastContactDate),
    });
    setIsDialogOpen(true);
  };

  const handleDelete = (id: number) => {
    if (confirm("Are you sure you want to delete this prospect?")) {
      deleteProspectMutation.mutate(id);
    }
  };

  const handleScheduleMeeting = (prospect: Prospect) => {
    calendarService.scheduleMeeting({
      type: "prospect",
      id: prospect.id,
      name: prospect.name,
      email: prospect.contactEmail || undefined,
      phone: prospect.contactPhone || undefined,
    });
  };

  const filteredProspects = prospects
    .filter((prospect) => {
      const matchesSearch =
        prospect.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        prospect.contactEmail.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus =
        filterStatus === "all" || prospect.status === filterStatus;
      const matchesPriority =
        filterPriority === "all" || prospect.priority === filterPriority;
      const matchesSource =
        filterSource === "all" || (prospect.source || "") === filterSource;
      return matchesSearch && matchesStatus && matchesPriority && matchesSource;
    })
    .sort((a, b) => {
      if (!sortField || !sortDirection) return 0;
      
      let aValue: string | number = "";
      let bValue: string | number = "";
      
      switch (sortField) {
        case "name":
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case "contactEmail":
          aValue = a.contactEmail.toLowerCase();
          bValue = b.contactEmail.toLowerCase();
          break;
        case "status":
          aValue = a.status;
          bValue = b.status;
          break;
        case "priority":
          const priorityOrder = { high: 1, medium: 2, low: 3 };
          aValue = priorityOrder[a.priority as keyof typeof priorityOrder] || 99;
          bValue = priorityOrder[b.priority as keyof typeof priorityOrder] || 99;
          break;
        case "expectedValue":
          aValue = parseFloat(a.expectedValue || "0");
          bValue = parseFloat(b.expectedValue || "0");
          break;
        case "source":
          aValue = (a.source || "").toLowerCase();
          bValue = (b.source || "").toLowerCase();
          break;
      }
      
      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

  const getStatusColor = (status: string) => {
    switch (status) {
      case "lead":
        return "bg-blue-100 text-blue-800";
      case "qualified":
        return "bg-green-100 text-green-800";
      case "proposal_sent":
        return "bg-yellow-100 text-yellow-800";
      case "negotiating":
        return "bg-orange-100 text-orange-800";
      case "closed_won":
        return "bg-emerald-100 text-emerald-800";
      case "closed_lost":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high":
        return "bg-red-100 text-red-800";
      case "medium":
        return "bg-yellow-100 text-yellow-800";
      case "low":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Prospects</h2>
            <p className="text-gray-600 mt-2">
              Manage your prospects and potential clients
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={exportTemplate}>
              <Download className="h-4 w-4 mr-2" />
              Export Template
            </Button>
            <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
              <DialogTrigger asChild>
                <PermissionButton
                  permission="prospects:create"
                  whenDenied="hide"
                  variant="outline"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Import
                </PermissionButton>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Import Prospects</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Upload a CSV file to import multiple prospects at once. 
                    Use the "Export Template" button to download a sample file with the correct format.
                  </p>
                  <div className="space-y-2">
                    <Input
                      type="file"
                      accept=".csv"
                      onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                    />
                    {importFile && (
                      <p className="text-sm text-green-600">
                        Selected: {importFile.name}
                      </p>
                    )}
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsImportDialogOpen(false);
                        setImportFile(null);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button onClick={handleImportFile} disabled={isImporting || !importFile}>
                      {isImporting ? "Importing..." : "Import Prospects"}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <PermissionButton
                  permission="prospects:create"
                  whenDenied="hide"
                  onClick={() => {
                    setEditingProspect(null);
                    form.reset();
                    setSelectedTeamId(null);
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Prospect
                </PermissionButton>
              </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingProspect ? "Edit Prospect" : "Add New Prospect"}
                </DialogTitle>
              </DialogHeader>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(handleSubmitForm)}
                  className="space-y-4"
                >
                  <div className="grid grid-cols-3 gap-4">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Company Name</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Enter company name"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="primaryContactName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Contact Name</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="John Doe"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="contactEmail"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Contact Email</FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              placeholder="contact@company.com"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField
                      control={form.control}
                      name="contactPhone"
                      render={({ field }) => {
                        const handleContactPhoneChange = (
                          event: ChangeEvent<HTMLInputElement>
                        ) => {
                          handlePhoneFieldChange(event, field.onChange);
                        };

                        return (
                          <FormItem>
                            <FormLabel>Contact Phone</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                value={field.value || ""}
                                placeholder="(555) 123-4567"
                                inputMode="tel"
                                maxLength={16}
                                onChange={handleContactPhoneChange}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        );
                      }}
                    />
                  </div>

                  <AddressFields control={form.control} />

                  <div className="grid grid-cols-3 gap-4">
                    <FormField
                      control={form.control}
                      name="status"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Status</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select status" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="lead">Lead</SelectItem>
                              <SelectItem value="qualified">
                                Qualified
                              </SelectItem>
                              <SelectItem value="proposal_sent">
                                Proposal Sent
                              </SelectItem>
                              <SelectItem value="negotiating">
                                Negotiating
                              </SelectItem>
                              <SelectItem value="closed_won">
                                Closed Won
                              </SelectItem>
                              <SelectItem value="closed_lost">
                                Closed Lost
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="priority"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Priority</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select priority" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="high">High</SelectItem>
                              <SelectItem value="medium">Medium</SelectItem>
                              <SelectItem value="low">Low</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="source"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Source</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value || ""}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select source" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {Object.entries(SOURCE_OPTIONS).map(
                                ([value, label]) => (
                                  <SelectItem key={value} value={value}>
                                    {label}
                                  </SelectItem>
                                )
                              )}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <FormField
                      control={form.control}
                      name="expectedValue"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Expected Value</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="25000.00"
                              {...field}
                              value={field.value || ""}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="expectedCloseDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Expected Close Date</FormLabel>
                          <FormControl>
                            <Input type="date" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="lastContactDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Last Contact Date</FormLabel>
                          <FormControl>
                            <Input type="date" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Producer/Team selector - shown when user is on multiple teams */}
                  {!editingProspect && userTeams.length > 1 && (
                    <div>
                      {/* A plain Label: this picker is React state, not a form field, and
                          FormLabel throws outside <FormField> — which crashed the whole
                          dialog for anyone on more than one team. */}
                      <Label>Producer / Team</Label>
                      <Select
                        value={selectedTeamId ? String(selectedTeamId) : ""}
                        onValueChange={(value) => setSelectedTeamId(value ? Number(value) : null)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select producer / team" />
                        </SelectTrigger>
                        <SelectContent>
                          {userTeams.map((team) => (
                            <SelectItem key={team.teamId} value={String(team.teamId)}>
                              {team.teamLeadName ? `${team.teamLeadName} — ${team.teamName}` : team.teamName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <FormField
                    control={form.control}
                    name="notes"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Notes</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Additional notes about this prospect..."
                            {...field}
                            value={field.value || ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={
                        createProspectMutation.isPending ||
                        updateProspectMutation.isPending
                      }
                    >
                      {editingProspect ? "Update Prospect" : "Create"}
                    </Button>
                  </div>
                </form>
              </Form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center space-x-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search prospects..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex items-center gap-2">
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="lead">Lead</SelectItem>
                    <SelectItem value="qualified">Qualified</SelectItem>
                    <SelectItem value="proposal_sent">Proposal Sent</SelectItem>
                    <SelectItem value="negotiating">Negotiating</SelectItem>
                    <SelectItem value="closed_won">Closed Won</SelectItem>
                    <SelectItem value="closed_lost">Closed Lost</SelectItem>
                  </SelectContent>
                </Select>

                <Select
                  value={filterPriority}
                  onValueChange={setFilterPriority}
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priorities</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={filterSource} onValueChange={setFilterSource}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="Source" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sources</SelectItem>
                    {Object.entries(SOURCE_OPTIONS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-6">
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center space-x-4">
                      <div className="h-12 w-12 rounded-full bg-gray-200 animate-pulse" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-gray-200 rounded animate-pulse" />
                        <div className="h-3 bg-gray-200 rounded w-2/3 animate-pulse" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                {filteredProspects.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <p className="mb-4">No prospects</p>
                    <PermissionButton
                      permission="prospects:create"
                      whenDenied="hide"
                      onClick={() => {
                        setEditingProspect(null);
                        form.reset();
                        setIsDialogOpen(true);
                      }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Prospect
                    </PermissionButton>
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={() => handleSort("name")}
                        >
                          <div className="flex items-center">
                            Company
                            {getSortIcon("name")}
                          </div>
                        </th>
                        <th
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={() => handleSort("contactEmail")}
                        >
                          <div className="flex items-center">
                            Contact
                            {getSortIcon("contactEmail")}
                          </div>
                        </th>
                        <th
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={() => handleSort("status")}
                        >
                          <div className="flex items-center">
                            Status
                            {getSortIcon("status")}
                          </div>
                        </th>
                        <th
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={() => handleSort("priority")}
                        >
                          <div className="flex items-center">
                            Priority
                            {getSortIcon("priority")}
                          </div>
                        </th>
                        <th
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={() => handleSort("expectedValue")}
                        >
                          <div className="flex items-center">
                            Expected Value
                            {getSortIcon("expectedValue")}
                          </div>
                        </th>
                        <th
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={() => handleSort("source")}
                        >
                          <div className="flex items-center">
                            Source
                            {getSortIcon("source")}
                          </div>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Primary Agent
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredProspects.map((prospect) => (
                        <tr key={prospect.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                <Avatar>
                                  <AvatarFallback>
                                    <Building className="h-5 w-5" />
                                  </AvatarFallback>
                                </Avatar>
                              </div>
                              <div className="ml-4">
                                <Link
                                  href={
                                    prospect.status === "closed_won" &&
                                    (prospect as any).clientId
                                      ? `/clients/${(prospect as any).clientId}`
                                      : `/prospects/${prospect.id}`
                                  }
                                >
                                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 hover:text-blue-600 cursor-pointer">
                                    {prospect.name}
                                  </div>
                                </Link>
                                <div className="text-sm text-gray-500 dark:text-gray-400"></div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900 dark:text-gray-100">
                              {prospect.contactEmail ? (
                                <button
                                  type="button"
                                  className="text-blue-600 hover:underline"
                                  onClick={() =>
                                    messagesService.compose({
                                      to: prospect.contactEmail!,
                                      linkTo: {
                                        type: "prospect",
                                        id: prospect.id,
                                      },
                                    })
                                  }
                                >
                                  {prospect.contactEmail}
                                </button>
                              ) : (
                                <span className="text-gray-500 dark:text-gray-400">No email</span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">
                              {prospect.contactPhone || "No phone"}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                                prospect.status
                              )}`}
                            >
                              {prospect.status.replace("_", " ")}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(
                                prospect.priority
                              )}`}
                            >
                              {prospect.priority}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                            {prospect.expectedValue
                              ? `$${parseFloat(
                                  prospect.expectedValue
                                ).toLocaleString()}`
                              : "Not set"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                            {formatSource(prospect.source)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                            {primaryProducerMap.get(prospect.id) || (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                            {prospect.status === "closed_won" &&
                            (prospect as any).clientId ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button variant="ghost" size="sm" asChild>
                                    <Link
                                      href={`/clients/${(prospect as any).clientId}`}
                                    >
                                      <Building className="h-4 w-4" />
                                    </Link>
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent variant="prominent">
                                  View Client
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              <>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button variant="ghost" size="sm" asChild>
                                      <Link href={`/prospects/${prospect.id}`}>
                                        <Target className="h-4 w-4" />
                                      </Link>
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent variant="prominent">
                                    View Prospect
                                  </TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() =>
                                        handleScheduleMeeting(prospect)
                                      }
                                    >
                                      <Calendar className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent variant="prominent">
                                    Schedule Meeting
                                  </TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <PermissionButton
                                      permission="prospects:edit"
                                      whenDenied="hide"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleEdit(prospect)}
                                    >
                                      <Edit className="h-4 w-4" />
                                    </PermissionButton>
                                  </TooltipTrigger>
                                  <TooltipContent variant="prominent">
                                    Edit Prospect
                                  </TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button variant="ghost" size="sm" asChild>
                                      <Link
                                        href={`/prospects/${prospect.id}?tab=contact-history`}
                                      >
                                        <MessageSquare className="h-4 w-4" />
                                      </Link>
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent variant="prominent">
                                    Contact History
                                  </TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <PermissionButton
                                      permission="prospects:delete"
                                      whenDenied="hide"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDelete(prospect.id)}
                                      className="text-red-600 hover:text-white hover:bg-red-600"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </PermissionButton>
                                  </TooltipTrigger>
                                  <TooltipContent variant="prominent">
                                    Delete Prospect
                                  </TooltipContent>
                                </Tooltip>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </TooltipProvider>
  );
}
